import { Trans, useLingui } from '@lingui/react/macro';
import React, { ReactNode, useId } from 'react';
import UDText from 'ud-ui/components/text';

export interface GuideStepProps {
  number: number;
  title: ReactNode;
  children: ReactNode;
  /** Скриншоты «куда нажать» по порядку кликов; нет — заглушка до совместного прогона. */
  clickShots?: string[];
  /** Скриншот «что должно получиться»; `null` — видимого результата у шага нет, слот не показывается. */
  resultShot?: string | null;
}

/** Шаг инструкции: номер, что сделать, скриншоты «куда нажать» и как выглядит результат. */
function GuideStep({ number, title, children, clickShots = [], resultShot }: GuideStepProps) {
  const { t } = useLingui();
  const titleId = useId();

  return (
    <article className="bg-surfaceCard rounded-card flex gap-4 p-5" aria-labelledby={titleId}>
      <div className="bg-surfaceSunken rounded-pill flex h-8 w-8 shrink-0 items-center justify-center">
        <UDText variant="subtitle-1-bold">{number}</UDText>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <UDText as="h3" variant="subtitle-1-bold" id={titleId}>
          {title}
        </UDText>
        <div className="text-inkPrimary flex flex-col gap-2">{children}</div>

        <div className="flex flex-wrap items-start gap-3">
          {clickShots.length > 0 ? (
            clickShots.map((src, index) => {
              const order = index + 1;
              return <GuideShot key={src} src={src} caption={clickShots.length > 1 ? t`Куда нажать · ${order}` : t`Куда нажать`} />;
            })
          ) : (
            <GuideShot caption={t`Куда нажать`} />
          )}
          {resultShot !== null && <GuideShot src={resultShot} caption={t`Что должно получиться`} />}
        </div>
      </div>
    </article>
  );
}

interface GuideShotProps {
  src?: string;
  caption: string;
}

function GuideShot({ src, caption }: GuideShotProps) {
  return (
    <figure className="flex max-w-full flex-col gap-1">
      {src ? (
        // Не растягиваем мелкие окна и не даём крупным занять экран — по клику полный размер.
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={caption} className="border-lineSubtle rounded-card max-h-[520px] w-auto max-w-full border" />
        </a>
      ) : (
        <div className="bg-surfaceSunken rounded-card flex h-32 w-80 max-w-full items-center justify-center p-4">
          <UDText variant="body-3" className="text-inkSecondary text-center">
            <Trans>Скриншот появится после проверки</Trans>
          </UDText>
        </div>
      )}
      <figcaption>
        <UDText variant="text-3" className="text-inkSecondary">
          {caption}
        </UDText>
      </figcaption>
    </figure>
  );
}

export default React.memo(GuideStep);
